import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const Pagination = ({
  pagination,
  onPageChange, // Optional additional callback
  onPageSizeChange, // Optional additional callback
  showInfo = true
}) => {
  const { currentPage, totalPages, totalItems, pageSize, setCurrentPage, setPageSize } = pagination;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (onPageChange) onPageChange(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    const sizeNum = parseInt(newSize, 10);
    setPageSize(sizeNum);
    setCurrentPage(0); // Reset to first page when size changes
    if (onPageSizeChange) onPageSizeChange(sizeNum);
  };

  const startItem = totalItems === 0 ? 0 : currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-2 mt-4">
      {/* Left side: Results Info & Rows Selection */}
      <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start order-2 sm:order-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
            Rows:
          </span>
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-7 w-[60px] text-[10px] md:text-xs border-slate-200 dark:border-yale-blue-800 bg-transparent font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={size.toString()} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showInfo && totalItems > 0 && (
          <p className="text-[10px] md:text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-yale-blue-800/50 px-2 py-1 rounded-md">
            {`${startItem}-${endItem}`} <span className="opacity-50">of</span> {totalItems}
          </p>
        )}
      </div>

      {/* Right side: Navigation */}
      <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end order-1 sm:order-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="h-8 w-8 md:h-9 md:w-auto md:px-3 border border-slate-200 dark:border-yale-blue-800 hover:bg-slate-100 dark:hover:bg-yale-blue-800"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden md:inline ml-1 text-xs font-medium">Previous</span>
        </Button>

        <div className="flex flex-col items-center min-w-[80px]">
          <span className="text-[10px] font-bold text-yale-blue-600 dark:text-yale-blue-400 uppercase tracking-widest">Page</span>
          <span className="text-xs font-bold text-slate-900 dark:text-slate-100">
            {currentPage + 1} <span className="text-slate-400 font-normal">/</span> {Math.max(1, totalPages)}
          </span>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="h-8 w-8 md:h-9 md:w-auto md:px-3 border border-slate-200 dark:border-yale-blue-800 hover:bg-slate-100 dark:hover:bg-yale-blue-800"
        >
          <span className="hidden md:inline mr-1 text-xs font-medium">Next</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
